"use client";

import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Ellipsis, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

export const buttonVariants = cva(
  [
    'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'transition-[color,background-color,border-color,box-shadow] duration-[var(--duration-fast)]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-]):not([class*="_h-"])]:size-4',
  ],
  {
    variants: {
      variant: {
        /** The page's main action. One per view. */
        default:
          'bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active',
        /** Any other action: outlined, quiet. */
        secondary:
          'border border-border-strong bg-surface text-foreground hover:border-input hover:bg-subtle active:bg-muted',
        /** Destroys or removes something. */
        destructive:
          'bg-danger text-danger-foreground hover:bg-danger/90 active:bg-danger/80',
        /** Low-emphasis action inside dense layouts and toolbars. */
        ghost:
          'text-muted-foreground hover:bg-muted hover:text-foreground active:bg-emphasis',
        /** An inline text action. */
        link: 'h-auto px-0 text-accent underline-offset-4 hover:underline active:text-accent-active',
      },
      size: {
        default: 'h-9 px-3.5',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-5',
        icon: 'size-9',
        'icon-sm': 'size-8',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Work is in progress: the button keeps its width and its label (as its
   * accessible name), shows a spinner in the label's place, announces itself
   * as busy and ignores further presses. Nothing around it moves.
   */
  loading?: boolean;
  children?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, onClick, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), loading && 'cursor-progress', className)}
        disabled={asChild ? undefined : disabled}
        aria-disabled={asChild && disabled ? true : undefined}
        aria-busy={loading || undefined}
        data-loading={loading || undefined}
        onClick={
          loading
            ? (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()
            : onClick
        }
        {...props}
      >
        {/* With asChild, Slot needs Slottable as a direct child. */}
        {asChild && loading ? <LoadingMark /> : null}
        {asChild ? <Slottable>{children}</Slottable> : null}
        {!asChild && loading ? (
          <span className="absolute inset-0 grid place-items-center">
            <LoadingMark />
          </span>
        ) : null}
        {/* The label keeps its place (and the button its width) while the
            spinner shows; it stays the button's accessible name. */}
        {asChild ? null : (
          <span className={cn('contents', loading && 'text-transparent')}>{children}</span>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

/**
 * Work in progress: a turning spinner, or, for someone who asked for reduced
 * motion, a still ellipsis that reads as "working" without moving.
 */
function LoadingMark() {
  return (
    <>
      <Loader2 className="animate-spin motion-reduce:hidden" aria-hidden="true" />
      <Ellipsis className="hidden motion-reduce:block" aria-hidden="true" data-loading-still="" />
    </>
  );
}
